import React, { useState } from "react";
import { motion } from "motion/react";
import styled from 'styled-components';

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  width: 100%;
  padding-top: 1.5rem;
`;

const AnimatedLabel = styled(motion.div)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  left: 0;
  z-index: 1;
`;

const AnimatedLetter = styled(motion.span)`
  display: inline-block;
  font-size: 0.875rem;
  will-change: transform;
  color: ${props => props.theme.colors.lightText};
`;

const StyledAnimatedInput = styled.input`
  outline: none;
  border: none;
  border-bottom: 2px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border};
  padding: 0.5rem 0;
  width: 100%;
  font-size: 1rem;
  font-weight: 500;
  background: transparent;
  color: ${props => props.theme.colors.text};
  transition: border-color 0.2s ease;
  
  &:focus {
    border-bottom-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.primary};
  }
  
  &::placeholder {
    color: transparent;
  }
`;

const StyledAnimatedTextarea = styled.textarea`
  outline: none;
  border: none;
  border-bottom: 2px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border};
  padding: 0.5rem 0;
  width: 100%;
  font-size: 1rem;
  font-weight: 500;
  background: transparent;
  color: ${props => props.theme.colors.text};
  transition: border-color 0.2s ease;
  min-height: 120px;
  resize: vertical;

  &:focus {
    border-bottom-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(248, 250, 252, 0.45);
    opacity: 1;
  }
`;

const TextareaContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  width: 100%;
`;

const TextareaLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.lightText};
  margin-bottom: 0.65rem;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const letterVariants = {
  initial: {
    y: 0,
    opacity: 1,
  },
  animate: {
    y: "-200%",
    opacity: 0.7,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
};

const AnimatedFieldBase = ({ label, value, error, children }) => {
  const [isFocused, setIsFocused] = useState(false);
  const showLabel = isFocused || (value && value.length > 0);

  const enhancedChild = React.cloneElement(children, {
    ...children.props,
    onFocus: (event) => {
      setIsFocused(true);
      if (children.props.onFocus) {
        children.props.onFocus(event);
      }
    },
    onBlur: (event) => {
      setIsFocused(false);
      if (children.props.onBlur) {
        children.props.onBlur(event);
      }
    },
    value,
    error,
  });

  return (
    <InputContainer>
      <AnimatedLabel
        variants={containerVariants}
        initial="initial"
        animate={showLabel ? "animate" : "initial"}
      >
        {label.split("").map((char, index) => (
          <AnimatedLetter
            key={index}
            variants={letterVariants}
          >
            {char === " " ? "\u00A0" : char}
          </AnimatedLetter>
        ))}
      </AnimatedLabel>

      {enhancedChild}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputContainer>
  );
};

export const AnimatedInput = React.forwardRef(({ label, value = '', type = 'text', error, ...props }, ref) => {
  return (
    <AnimatedFieldBase label={label} value={value} error={error}>
      <StyledAnimatedInput ref={ref} type={type} {...props} />
    </AnimatedFieldBase>
  );
});

export const AnimatedTextarea = ({
  label,
  value = '',
  error,
  id,
  ...props
}) => {
  return (
    <TextareaContainer>
      <TextareaLabel htmlFor={id}>{label}</TextareaLabel>
      <StyledAnimatedTextarea id={id} value={value} error={error} {...props} />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </TextareaContainer>
  );
};